// api.js — backend router (Sheets→Supabase migration, hybrid phase).
// ===========================================================================
// Re-exports the legacy Apps Script implementation (apiLegacy.js) for EVERY
// endpoint, then overrides the migrated ones with the Supabase implementation
// (apiSupabase.js). ESM rule: an explicit named re-export shadows the matching
// name from `export *`, so the Supabase versions win for the endpoints listed
// below; everything else still flows to /exec.
//
// This is the cutover artifact: as more endpoints move to Supabase, add their
// names to the apiSupabase re-export block. When the list is complete, the
// `export * from './apiLegacy'` line can be dropped and apiLegacy.js deleted.
//
// SAFETY: components import from THIS file unchanged. apiSupabase's reads are
// RLS-scoped to the signed-in Supabase session; legacy endpoints still use the
// GAS token in localStorage.3c_token. Both auth contexts are established at
// login (see DevLogin in the preview, or the hub + a Supabase sign-in at
// cutover).
// ===========================================================================

import * as _sb from './apiSupabase';
import { trackWrite } from './updateGuard';

// Everything (legacy GAS) — the baseline.
export * from './apiLegacy';

// Migrated to Supabase — READS (and AI queries). These names override the
// legacy ones above and are re-exported untouched.
export {
  verifyPin,
  verifySecretWord,
  getBookingsRange,
  getBookings,
  moveBookingSite,
  getAllCustomers,
  searchCustomers,
  customerStats,
  customerSegmentBounds,
  getStock,
  getStockLog,
  getChecklist,
  listTasks,
  listShopNeeds,
  listWaitlist,
  lockWaitlistSlot,
  releaseWaitlistSlot,
  pollWebsiteLeads,
  backfillMembershipNotifs,
  ensureWinterClubLinks,
  ensureBackseatLinks,
  backfillMembershipRevenue,
  auditInvoiceLinks,
  purgeOldMedia,
  getBlocks,
  getTimeOff,
  getGiftCards,
  getNotifications,
  listNotificationHistory,
  notifyStaff,
  getReminders,
  getStuckReminderCount,
  getVipInbox,
  getRevenueSummary,
  getEmailDrafts,
  getDashboardOverview,
  getWeeklyGoal,
  getAnnualGoal,
  getEquipmentUsage,
  listMembers,
  listCustomerTabs,
  listStorageSubs,
  saveConditionCheck,
  listConditionChecks,
  listCareFleet,
  listCareOutreach,
  addCareSuggestions,
  updateCareOutreach,
  getCareConfig,
  saveCareConfig,
  careLastTexted,
  listUpsellOffers,
  storageMonthNow,
  storageMonthAdd,
  storageMonthLabel,
  storageMonthSpan,
  getDealerConfig,
  listDealers,
  listDealerCustomers,
  listDealerSchedule,
  listDealerRequests,
  listDealerInvoices, listDealerBiohazardNotices, listDealerFeeNotices, listDeclinedBookingRequests, getDepositLedger,
  getPaymentTracker,
  getCustomerProfile,
  searchDealerCustomers,
  listMechServices,
  getVehicleById,
  getSiteBookedDays,
  getMembershipConfig,
  listRecurringExpenses,
  listPendingBookingRequests,
  getTrash,
  listPoolPeople,
  listPoolTasks,
  listPoolShifts,
  getStaffRoster,
  getStaffRosterAdmin,
  listCustomerRules,
  listCustomerPackages,
  listBlasts,
  blastAlreadySent,
  blastReachedKeys,
  listBlastPresets,
  blastLedgerCounts,
  unfinishedBlast,
  listCheckoutChecklist,
  listTodayCheckins,
  listExpenses,
  listExpenseVendors,
  listVendorsRollup,
  listMileage,
  listEmployees,
  getTimeclock,
  listEmployeeWeeks,
  getEmployeeArchive,
  getLaborSummary,
  listBankStatements,
  listBankTransactions,
  getReconciliationSummary,
  listUnmatchedBankTxns,
  getMarketingData,
  askMarketingAgent,
  getEngineerData,
  askOwnerAI,
  askPrincipalAI,
  askDetailerAI,
} from './apiSupabase';

// Migrated to Supabase — WRITES. Each is wrapped with trackWrite() so the
// seamless-update controller (public/index.html + src/lib/updateGuard.js)
// never reloads the page while one of these mutations is in flight. The
// legacy /exec writes are already bracketed inside apiLegacy.post().
const _SB_WRITES = [
  'createBooking', 'updateBooking', 'updateBookingStatus', 'cancelBooking',
  'deleteBooking', 'pushBookingToToday',
  'updateCustomer', 'upsertCustomer', 'deleteCustomer',
  'addVehicle', 'updateVehicle', 'deleteVehicle',
  'updateStockLevel', 'saveStockItem', 'deleteStockItem', 'notifyLowStock', 'logStockChange',
  'copyStockToSite',
  'tickTask', 'untickTask', 'addCustomTask', 'removeChecklistTask', 'amendChecklist',
  'saveTars', 'requestReview', 'approveReview',
  'addTask', 'updateTask', 'markTaskDone', 'reopenTask', 'deleteTask', 'remindTask',
  'saveReminder', 'setReminderStatus', 'snoozeReminder', 'deleteReminder', 'runDueReminders', 'backfillAppointmentReminders',
  'scheduleReadyText', 'runReadyTexts', 'ebsBroadcast',
  'addShopNeed', 'reviewShopNeed', 'deleteShopNeed',
  'addWaitlist', 'removeWaitlist',
  'addBlock', 'removeBlock', 'addTimeOff', 'removeTimeOff',
  'sendSms',
  'generatePreDraft', 'regeneratePreDraft', 'generatePostDraft', 'generateBalanceDraft', 'generateRescheduleDraft', 'produceRescheduleEmail', 'draftBalanceRequest',
  'buildEodDrafts', 'updateEmailDraft', 'deleteEmailDraft', 'purgeEmailDraft', 'reopenEmailDraft', 'createReplyDraft',
  'createComebackDraft', 'sendEmailDraft', 'sendDepositRequestEmail', 'sendBalanceRequestEmail',
  'syncInvoices', 'syncCustomers',
  'saveWeeklyGoal', 'saveAnnualGoal', 'saveEquipmentUsage',
  'addMember', 'updateMember', 'removeMember', 'sendMembershipSignup', 'saveMembershipConfig', 'billMemberNow',
  'addCustomerTab', 'renameCustomerTab', 'removeCustomerTab', 'addTabEntry', 'deleteTabEntry', 'clearCustomerTabEntries',
  'saveStorageSub', 'setStorageStatus', 'removeStorageSub', 'sendStorageSignup', 'storagePaymentLink', 'storagePortalLink', 'postStorageNow', 'cancelStorageSub', 'storageVehicleAction', 'sendStoragePortalLink', 'approveStoragePayment',
  'createUpsellDraft', 'setUpsellOutcome', 'withdrawUpsellOffer', 'acceptUpsellOffer',
  'addDealerCustomer', 'updateDealerCustomerContact', 'deleteDealerCustomer', 'submitDealerBookingRequest', 'setDealerDiscount', 'setDealerPhone',
  'setCustomerRedFlag', 'purgeCustomerMedia',
  'createGiftCard', 'updateGiftCard', 'updateGiftCardStatus', 'sendGiftCardEmail',
  'addExpense', 'updateExpense', 'deleteExpense',
  'addMileage', 'updateMileage', 'deleteMileage',
  'addRecurringExpense', 'updateRecurringExpense', 'deleteRecurringExpense', 'runDueRecurringExpenses',
  'addEmployee', 'updateEmployee', 'deleteEmployee', 'saveTimeclock',
  'uploadBankStatement', 'parseBankStatement', 'convertBankTxnToExpense', 'setBankTxnIgnored',
  'deleteBankStatement', 'setBankStatementSource', 'linkExpenseToBankTxn', 'unlinkExpenseFromBankTxn',
  'saveMarketingCategory', 'deleteMarketingCategory',
  'engineerSaveScratchpad', 'engineerSavePrivateScratchpad', 'engineerMarkBugFixed',
  'engineerSetMaintenanceMode', 'engineerForceLogoutAll', 'engineerDeleteHeatmapCombo',
  'updateInvoiceRow', 'deleteInvoiceRow', 'linkInvoiceToBooking', 'unlinkInvoiceFromBooking',
  'resyncBookingLink', 'linkBookingSo', 'unlinkBookingSo',
  'dismissNotification', 'clearBookingRequestNotifications', 'deleteVipNote',
  'restoreTrashItem', 'purgeTrashItem', 'emptyTrash',
  'savePoolPerson', 'deletePoolPerson', 'addPoolTask', 'updatePoolTask', 'setPoolTaskDone', 'deletePoolTask',
  'addPoolShift', 'updatePoolShift', 'ackPoolShift', 'deletePoolShift',
  'saveCustomerRule', 'deleteCustomerRule', 'setCustomerRulesFor',
  'saveCustomerPackage', 'removeCustomerPackage', 'reopenCustomerPackage',
  'generateBlastCopy', 'saveBlast', 'recordBlastSend', 'finishBlast', 'deleteBlast', 'sendBlastEmail',
  'saveBlastPreset', 'touchBlastPreset', 'deleteBlastPreset',
  'staffCheckIn', 'staffCheckOut', 'setStaffPin', 'staffMarkAbsent', 'staffClearAbsent',
  'saveCheckoutChecklistItem', 'deleteCheckoutChecklistItem',
  'setVipChecklistVisible',
  'approveBookingRequest', 'rejectBookingRequest', 'dismissBookingRequest',
  'requestDeposit', 'markDepositPaid', 'markDepositRefunded', 'requestBalance', 'markBalancePaid',
  'setBookingPaymentCleared', 'withdrawDealerRequest',
  'setDealerInvoicePaid', 'setDealerBillingEmail', 'resendDealerInvoice', 'createDealerInvoice', 'voidDealerInvoice', 'setDealerArrival', 'setDealerCancel', 'setDealerBiohazard', 'ackDealerBiohazard', 'setDealerSoilFee', 'ackDealerFee', 'setBookingRequestFollowup', 'balanceDraftOptions',
  'setTaskState', 'setVehicleServiceFacts', 'saveMechService', 'deleteMechService',
];

const _wrapped = {};
_SB_WRITES.forEach((name) => {
  const fn = _sb[name];
  _wrapped[name] = (...args) => trackWrite(fn(...args));
});

export const createBooking = _wrapped.createBooking;
export const updateBooking = _wrapped.updateBooking;
export const updateBookingStatus = _wrapped.updateBookingStatus;
export const cancelBooking = _wrapped.cancelBooking;
export const deleteBooking = _wrapped.deleteBooking;
export const pushBookingToToday = _wrapped.pushBookingToToday;
export const updateCustomer = _wrapped.updateCustomer;
export const upsertCustomer = _wrapped.upsertCustomer;
export const deleteCustomer = _wrapped.deleteCustomer;
export const addVehicle = _wrapped.addVehicle;
export const updateVehicle = _wrapped.updateVehicle;
export const deleteVehicle = _wrapped.deleteVehicle;
export const updateStockLevel = _wrapped.updateStockLevel;
export const saveStockItem = _wrapped.saveStockItem;
export const deleteStockItem = _wrapped.deleteStockItem;
export const copyStockToSite = _wrapped.copyStockToSite;
export const notifyLowStock = _wrapped.notifyLowStock;
export const logStockChange = _wrapped.logStockChange;
export const tickTask = _wrapped.tickTask;
export const untickTask = _wrapped.untickTask;
export const addCustomTask = _wrapped.addCustomTask;
export const removeChecklistTask = _wrapped.removeChecklistTask;
export const amendChecklist = _wrapped.amendChecklist;
export const saveTars = _wrapped.saveTars;
export const requestReview = _wrapped.requestReview;
export const approveReview = _wrapped.approveReview;
export const addTask = _wrapped.addTask;
export const updateTask = _wrapped.updateTask;
export const markTaskDone = _wrapped.markTaskDone;
export const reopenTask = _wrapped.reopenTask;
export const deleteTask = _wrapped.deleteTask;
export const remindTask = _wrapped.remindTask;
export const saveReminder = _wrapped.saveReminder;
export const setReminderStatus = _wrapped.setReminderStatus;
export const snoozeReminder = _wrapped.snoozeReminder;
export const deleteReminder = _wrapped.deleteReminder;
export const runDueReminders = _wrapped.runDueReminders;
export const backfillAppointmentReminders = _wrapped.backfillAppointmentReminders;
export const scheduleReadyText = _wrapped.scheduleReadyText;
export const runReadyTexts = _wrapped.runReadyTexts;
export const ebsBroadcast = _wrapped.ebsBroadcast;
export const addShopNeed = _wrapped.addShopNeed;
export const reviewShopNeed = _wrapped.reviewShopNeed;
export const deleteShopNeed = _wrapped.deleteShopNeed;
export const addWaitlist = _wrapped.addWaitlist;
export const removeWaitlist = _wrapped.removeWaitlist;
export const addBlock = _wrapped.addBlock;
export const removeBlock = _wrapped.removeBlock;
export const addTimeOff = _wrapped.addTimeOff;
export const removeTimeOff = _wrapped.removeTimeOff;
export const sendSms = _wrapped.sendSms;
export const generatePreDraft = _wrapped.generatePreDraft;
export const regeneratePreDraft = _wrapped.regeneratePreDraft;
export const generatePostDraft = _wrapped.generatePostDraft;
export const generateBalanceDraft = _wrapped.generateBalanceDraft;
export const generateRescheduleDraft = _wrapped.generateRescheduleDraft;
export const produceRescheduleEmail = _wrapped.produceRescheduleEmail;
export const draftBalanceRequest = _wrapped.draftBalanceRequest;
export const buildEodDrafts = _wrapped.buildEodDrafts;
export const updateEmailDraft = _wrapped.updateEmailDraft;
export const deleteEmailDraft = _wrapped.deleteEmailDraft;
export const purgeEmailDraft = _wrapped.purgeEmailDraft;
export const reopenEmailDraft = _wrapped.reopenEmailDraft;
export const createReplyDraft = _wrapped.createReplyDraft;
export const createComebackDraft = _wrapped.createComebackDraft;
export const sendEmailDraft = _wrapped.sendEmailDraft;
export const sendDepositRequestEmail = _wrapped.sendDepositRequestEmail;
export const sendBalanceRequestEmail = _wrapped.sendBalanceRequestEmail;
export const syncInvoices = _wrapped.syncInvoices;
export const syncCustomers = _wrapped.syncCustomers;
export const saveWeeklyGoal = _wrapped.saveWeeklyGoal;
export const saveEquipmentUsage = _wrapped.saveEquipmentUsage;
export const saveAnnualGoal = _wrapped.saveAnnualGoal;
export const addMember = _wrapped.addMember;
export const updateMember = _wrapped.updateMember;
export const removeMember = _wrapped.removeMember;
export const sendMembershipSignup = _wrapped.sendMembershipSignup;
export const saveMembershipConfig = _wrapped.saveMembershipConfig;
export const billMemberNow = _wrapped.billMemberNow;
export const addCustomerTab = _wrapped.addCustomerTab;
export const renameCustomerTab = _wrapped.renameCustomerTab;
export const removeCustomerTab = _wrapped.removeCustomerTab;
export const addTabEntry = _wrapped.addTabEntry;
export const deleteTabEntry = _wrapped.deleteTabEntry;
export const clearCustomerTabEntries = _wrapped.clearCustomerTabEntries;
export const saveStorageSub = _wrapped.saveStorageSub;
export const storageVehicleAction = _wrapped.storageVehicleAction;
export const approveStoragePayment = _wrapped.approveStoragePayment;
export const sendStoragePortalLink = _wrapped.sendStoragePortalLink;
export const setStorageStatus = _wrapped.setStorageStatus;
export const removeStorageSub = _wrapped.removeStorageSub;
export const sendStorageSignup = _wrapped.sendStorageSignup;
export const storagePaymentLink = _wrapped.storagePaymentLink;
export const storagePortalLink = _wrapped.storagePortalLink;
export const postStorageNow = _wrapped.postStorageNow;
export const cancelStorageSub = _wrapped.cancelStorageSub;
export const createUpsellDraft = _wrapped.createUpsellDraft;
export const setUpsellOutcome = _wrapped.setUpsellOutcome;
export const withdrawUpsellOffer = _wrapped.withdrawUpsellOffer;
export const acceptUpsellOffer = _wrapped.acceptUpsellOffer;
export const addDealerCustomer = _wrapped.addDealerCustomer;
export const updateDealerCustomerContact = _wrapped.updateDealerCustomerContact;
export const deleteDealerCustomer = _wrapped.deleteDealerCustomer;
export const submitDealerBookingRequest = _wrapped.submitDealerBookingRequest;
export const setDealerDiscount = _wrapped.setDealerDiscount;
export const setDealerPhone = _wrapped.setDealerPhone;
export const setCustomerRedFlag = _wrapped.setCustomerRedFlag;
export const purgeCustomerMedia = _wrapped.purgeCustomerMedia;
export const createGiftCard = _wrapped.createGiftCard;
export const updateGiftCard = _wrapped.updateGiftCard;
export const updateGiftCardStatus = _wrapped.updateGiftCardStatus;
export const sendGiftCardEmail = _wrapped.sendGiftCardEmail;
export const addExpense = _wrapped.addExpense;
export const updateExpense = _wrapped.updateExpense;
export const deleteExpense = _wrapped.deleteExpense;
export const addMileage = _wrapped.addMileage;
export const updateMileage = _wrapped.updateMileage;
export const deleteMileage = _wrapped.deleteMileage;
export const addRecurringExpense = _wrapped.addRecurringExpense;
export const updateRecurringExpense = _wrapped.updateRecurringExpense;
export const deleteRecurringExpense = _wrapped.deleteRecurringExpense;
export const runDueRecurringExpenses = _wrapped.runDueRecurringExpenses;
export const addEmployee = _wrapped.addEmployee;
export const updateEmployee = _wrapped.updateEmployee;
export const deleteEmployee = _wrapped.deleteEmployee;
export const saveTimeclock = _wrapped.saveTimeclock;
export const uploadBankStatement = _wrapped.uploadBankStatement;
export const parseBankStatement = _wrapped.parseBankStatement;
export const convertBankTxnToExpense = _wrapped.convertBankTxnToExpense;
export const setBankTxnIgnored = _wrapped.setBankTxnIgnored;
export const deleteBankStatement = _wrapped.deleteBankStatement;
export const setBankStatementSource = _wrapped.setBankStatementSource;
export const linkExpenseToBankTxn = _wrapped.linkExpenseToBankTxn;
export const unlinkExpenseFromBankTxn = _wrapped.unlinkExpenseFromBankTxn;
export const saveMarketingCategory = _wrapped.saveMarketingCategory;
export const deleteMarketingCategory = _wrapped.deleteMarketingCategory;
export const engineerSaveScratchpad = _wrapped.engineerSaveScratchpad;
export const engineerSavePrivateScratchpad = _wrapped.engineerSavePrivateScratchpad;
export const engineerMarkBugFixed = _wrapped.engineerMarkBugFixed;
export const engineerSetMaintenanceMode = _wrapped.engineerSetMaintenanceMode;
export const engineerForceLogoutAll = _wrapped.engineerForceLogoutAll;
export const engineerDeleteHeatmapCombo = _wrapped.engineerDeleteHeatmapCombo;
export const updateInvoiceRow = _wrapped.updateInvoiceRow;
export const deleteInvoiceRow = _wrapped.deleteInvoiceRow;
export const linkInvoiceToBooking = _wrapped.linkInvoiceToBooking;
export const unlinkInvoiceFromBooking = _wrapped.unlinkInvoiceFromBooking;
export const resyncBookingLink = _wrapped.resyncBookingLink;
export const linkBookingSo = _wrapped.linkBookingSo;
export const unlinkBookingSo = _wrapped.unlinkBookingSo;
export const dismissNotification = _wrapped.dismissNotification;
export const clearBookingRequestNotifications = _wrapped.clearBookingRequestNotifications;
export const deleteVipNote = _wrapped.deleteVipNote;
export const restoreTrashItem = _wrapped.restoreTrashItem;
export const purgeTrashItem = _wrapped.purgeTrashItem;
export const savePoolPerson = _wrapped.savePoolPerson;
export const deletePoolPerson = _wrapped.deletePoolPerson;
export const addPoolTask = _wrapped.addPoolTask;
export const updatePoolTask = _wrapped.updatePoolTask;
export const setPoolTaskDone = _wrapped.setPoolTaskDone;
export const deletePoolTask = _wrapped.deletePoolTask;
export const addPoolShift = _wrapped.addPoolShift;
export const updatePoolShift = _wrapped.updatePoolShift;
export const ackPoolShift = _wrapped.ackPoolShift;
export const deletePoolShift = _wrapped.deletePoolShift;
export const saveCustomerRule = _wrapped.saveCustomerRule;
export const deleteCustomerRule = _wrapped.deleteCustomerRule;
export const setCustomerRulesFor = _wrapped.setCustomerRulesFor;
export const saveCustomerPackage = _wrapped.saveCustomerPackage;
export const removeCustomerPackage = _wrapped.removeCustomerPackage;
export const reopenCustomerPackage = _wrapped.reopenCustomerPackage;
export const generateBlastCopy = _wrapped.generateBlastCopy;
export const saveBlast = _wrapped.saveBlast;
export const recordBlastSend = _wrapped.recordBlastSend;
export const finishBlast = _wrapped.finishBlast;
export const deleteBlast = _wrapped.deleteBlast;
export const sendBlastEmail = _wrapped.sendBlastEmail;
export const saveBlastPreset = _wrapped.saveBlastPreset;
export const touchBlastPreset = _wrapped.touchBlastPreset;
export const deleteBlastPreset = _wrapped.deleteBlastPreset;
export const staffCheckIn = _wrapped.staffCheckIn;
export const staffCheckOut = _wrapped.staffCheckOut;
export const setStaffPin = _wrapped.setStaffPin;
export const staffMarkAbsent = _wrapped.staffMarkAbsent;
export const staffClearAbsent = _wrapped.staffClearAbsent;
export const saveCheckoutChecklistItem = _wrapped.saveCheckoutChecklistItem;
export const deleteCheckoutChecklistItem = _wrapped.deleteCheckoutChecklistItem;
export const emptyTrash = _wrapped.emptyTrash;
export const setVipChecklistVisible = _wrapped.setVipChecklistVisible;
export const approveBookingRequest = _wrapped.approveBookingRequest;
export const rejectBookingRequest = _wrapped.rejectBookingRequest;
export const dismissBookingRequest = _wrapped.dismissBookingRequest;
export const requestDeposit = _wrapped.requestDeposit;
export const markDepositPaid = _wrapped.markDepositPaid;
export const markDepositRefunded = _wrapped.markDepositRefunded;
export { refundDeposit } from './apiSupabase';
export const requestBalance = _wrapped.requestBalance;
export const setDealerInvoicePaid = _wrapped.setDealerInvoicePaid;
export const setBookingPaymentCleared = _wrapped.setBookingPaymentCleared;
export const withdrawDealerRequest = _wrapped.withdrawDealerRequest;
export const setDealerBillingEmail = _wrapped.setDealerBillingEmail;
export const resendDealerInvoice = _wrapped.resendDealerInvoice;
export const createDealerInvoice = _wrapped.createDealerInvoice;
export const voidDealerInvoice = _wrapped.voidDealerInvoice;
export const markBalancePaid = _wrapped.markBalancePaid;
export const setDealerArrival = _wrapped.setDealerArrival;
export const setDealerCancel = _wrapped.setDealerCancel;
export const setDealerBiohazard = _wrapped.setDealerBiohazard;
export const ackDealerBiohazard = _wrapped.ackDealerBiohazard;
export const setDealerSoilFee = _wrapped.setDealerSoilFee;
export const ackDealerFee = _wrapped.ackDealerFee;
export const setBookingRequestFollowup = _wrapped.setBookingRequestFollowup;
export const balanceDraftOptions = _wrapped.balanceDraftOptions;
export const setTaskState = _wrapped.setTaskState;
export const setVehicleServiceFacts = _wrapped.setVehicleServiceFacts;
export const saveMechService = _wrapped.saveMechService;
export const deleteMechService = _wrapped.deleteMechService;
